import { createApp, watchEffect } from "vue";
import { createPinia } from "pinia";
import App from "./App.vue";
import router from "@/_helpers/router";
import { i18n } from "@/plugins/i18n";
import { createPersistedState } from "pinia-plugin-persistedstate";
import "@/assets/style.css";
import vuetify from "./plugins/vuetify";
import http from "@/plugins/https";
import VueNativeSock from "vue-native-websocket-vue3";
import { useSocketStore } from "@/_store/websocket.module";
import { createGtm } from "@gtm-support/vue-gtm";
import "moment/locale/es";
import "moment/locale/en-gb";
import "moment/locale/it";
import "moment/locale/fr";
import "moment/locale/fr-ca";
import "moment/locale/de";
import * as Sentry from "@sentry/vue";

const productionEnvs: ImportMetaEnv["VITE_CLIENT_ENV"][] = ["secure", "secure-ca", "secure-eu"];
const isProduction = productionEnvs.includes(import.meta.env.VITE_CLIENT_ENV);
const sentryRestrictedEnvironments: ImportMetaEnv["VITE_CLIENT_ENV"][] = ["development"];
const sentryEnabled = !sentryRestrictedEnvironments.includes(import.meta.env.VITE_CLIENT_ENV);
const sentryRestrictedUrls = [/localhost/i, /127\.0\.0\.1/i];
const sentryThirdPartyUrls = [
  /googletagmanager\.com/i,
  /google-analytics\.com/i,
  /gtag\/js/i,
  /chmln/i,
  /gtm/i,
  /hockeystack/i,
  /recaptcha/i,
  /gstatic\.com\/recaptcha/i,
];
const sentryDeniedUrls = [...sentryRestrictedUrls, ...sentryThirdPartyUrls];

const pinia = createPinia();
pinia.use(createPersistedState());
const app = createApp(App);

Sentry.init({
  app,
  // configured in gitlab CI/CD variables
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_CLIENT_ENV,
  release: import.meta.env.VITE_SENTRY_RELEASE,
  enabled: sentryEnabled,
  denyUrls: sentryDeniedUrls,
});

app.use(pinia);
app.use(router);
app.use(vuetify);
app.use(http);
app.use(i18n);
app.use(
  createGtm({
    id: "GTM-566LFN7", // Your GTM single container ID or array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy'] or array of objects [{id: 'GTM-xxxxxx', queryPararms: { gtm_auth: 'abc123', gtm_preview: 'env-4', gtm_cookies_win: 'x'}}, {id: 'GTM-yyyyyy', queryParams: {gtm_auth: 'abc234', gtm_preview: 'env-5', gtm_cookies_win: 'x'}}]
    defer: false, // Script can be set to `defer` to speed up page load at the cost of less accurate results (in case visitor leaves before script is loaded, which is unlikely but possible). Defaults to false, so the script is loaded `async` by default
    compatibility: false, // Will add `async` and `defer` to the script tag to not block requests for old browsers that do not support `async`
    enabled: isProduction, // defaults to true. Plugin can be disabled by setting this to false for Ex: enabled: !!GDPR_Cookie (optional)
    debug: false, // Whether or not display console logs debugs (optional)
    loadScript: true, // Whether or not to load the GTM Script (Helpful if you are including GTM manually, but need the dataLayer functionality in your components) (optional)
    vueRouter: router, // Pass the router instance to automatically sync with router (optional)
    ignoredViews: [], // Don't trigger events for specified router names (optional)
    trackOnNextTick: false, // Whether or not call trackView in Vue.nextTick
  })
);
// @ts-ignore after update vue-native-websocket-vue3 forces to use format in the config which is not needed for us
app.use(VueNativeSock, `${import.meta.env.VITE_WEBSOCKET_HOST}/websocket`, {
  store: useSocketStore(app),
  reconnection: true,
  reconnectionAttempts: 100,
  reconnectionDelay: 3000,
});

watchEffect(() => {
  document.documentElement.lang = i18n.global.locale.value;
});

app.mount("#app");
